import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Plus, ListChecks, Mail } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import useOpsBriefingLink from "../../hooks/useOpsBriefingLink";
import { OpsBriefingBookButton, OpsBriefingLinkEditor } from "./OpsBriefingBooking";

/**
 * Intake completion screen.
 *
 * The submit used to bounce straight to the submissions list; it now lands here
 * so the rep gets the one next action that isn't obvious from a toast — booking
 * the sales → ops kickoff while the project is still fresh.
 */
export default function IntakeSubmittedStep({ result, onNewIntake }) {
  const navigate = useNavigate();
  const link = useOpsBriefingLink();

  return (
    <Card data-testid="intake-submitted">
      <CardContent className="p-6 sm:p-8 space-y-6">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
              Intake submitted
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {result?.project_name
                ? <>“{result.project_name}” was created and is awaiting activation.</>
                : "The project was created and is awaiting activation."}
            </p>
          </div>
        </div>

        {/* Next step: book the sales → ops kickoff with the ops team. */}
        <div className="rounded-lg border bg-secondary/20 p-4 sm:p-5 space-y-3 text-center">
          <p className="micro-label text-muted-foreground">Next step</p>
          <p className="text-sm">
            Book the {link.calendarName}
            {link.hostName ? ` with ${link.hostName}` : ""} so ops can pick the project up.
          </p>
          {link.loading ? (
            <div className="h-9" />
          ) : link.configured ? (
            <div className="flex justify-center">
              <OpsBriefingBookButton link={link} />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-left text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  The ops booking calendar hasn’t been added to HQ yet
                  {link.canEdit
                    ? " — paste the calendar URL below and the button appears for everyone."
                    : " — ask an admin to add it on My Submissions. Reach out to ops directly in the meantime."}
                </span>
              </div>
              <OpsBriefingLinkEditor link={link} compact />
            </div>
          )}
        </div>

        {/* The client's planning call is ops' job now — reps stop sending that link. */}
        <div className="flex items-start gap-2 rounded-lg border p-4 text-left text-sm" data-testid="intake-submitted-planning-call-note">
          <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
          <span>
            Ops will send the client the planning call invite automatically once the project is activated, and
            follow up until they book. You don&apos;t need to send the client the planning call link.
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-center">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate("/sales/intake/submissions")}
            data-testid="intake-submitted-submissions-btn"
          >
            <ListChecks className="w-4 h-4 mr-2" />
            View my submissions
          </Button>
          <Button variant="ghost" className="rounded-full" onClick={onNewIntake}>
            <Plus className="w-4 h-4 mr-2" />
            Start another intake
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
